import { MouseEventHandler, createContext, useCallback, useContext, useState } from 'react';
import { MAGIC_REVEAL } from './blurPatterns';
import useLongPress from './useLongPress';
import useMagicReveal from './useMagicReveal';

export interface IMobileBlurSettings {
    /** The gallery's blur style; null when the gallery has no mobile screenshot protection */
    blurType: Nullable<SpApi.ScreenshotDeterrenceMobileStyle>;
    /** Where the blur applies: the grid and the lightbox, or only the lightbox */
    scope: SpApi.ScreenshotDeterrenceMobileScope;
    /** True on phones and tablets, or wherever a preview stands in for one */
    isMobile: boolean;
    /** In a preview nobody can touch, the one photo whose Magic Reveal demonstrates itself */
    demoPhotoId?: number;
}

export const NO_MOBILE_BLUR: IMobileBlurSettings = {
    blurType: null,
    scope: 'all',
    isMobile: false
};

export const MobileBlurContext = createContext(NO_MOBILE_BLUR);

const preventDefault: MouseEventHandler = (event) => event.preventDefault();

/**
 * Mobile screenshot protection for one photo surface. `blur` is set when the photo should be
 * blurred; render `MobileBlurImage` or `MobileBlurBackdrop` with it, and spread `pressHandlers`
 * on the element whose long press swaps the blurred half (or, for Magic Reveal, starts the ink
 * that a drag then spreads). `onContextMenu` blocks right-click-save on every device while the
 * gallery has mobile protection. `photoId` is only needed where a preview can demo the photo.
 */
const useMobileBlur = (surface: 'grid' | 'lightbox', photoId?: number) => {
    const { blurType, scope, isMobile, demoPhotoId } = useContext(MobileBlurContext);
    const [isSwapped, setIsSwapped] = useState(false);
    const isBlurred = isMobile && !!blurType && (surface === 'lightbox' || scope === 'all');
    const isInking = isBlurred && blurType === MAGIC_REVEAL;
    const { inkLayerRef, addInkPoint, demoFingerRef } = useMagicReveal(
        isInking && demoPhotoId !== undefined && photoId === demoPhotoId
    );
    const swap = useCallback(() => setIsSwapped((wasSwapped) => !wasSwapped), []);
    const pressHandlers = useLongPress(
        isInking ? addInkPoint : swap,
        isInking ? addInkPoint : undefined
    );

    return {
        blur: isBlurred ? { blurType, isSwapped, inkLayerRef, demoFingerRef } : null,
        pressHandlers: isBlurred ? pressHandlers : undefined,
        onContextMenu: blurType ? preventDefault : undefined
    };
};

export default useMobileBlur;
